import { z, ZodErrorMap } from 'zod/v3';
import {
  CODE_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
} from '../../constants';
import translations from './common.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { errorMap } from './errorMap';

const { addTranslations, t } = i18nNsFactory('classifier-mapping-mutate');
addTranslations(translations);

export const zCodeErrorMap: ZodErrorMap = (issue) => {
  if (issue.code === z.ZodIssueCode.too_big) {
    return { message: t('validation.too_big', { max: issue.maximum }) };
  } else if (
    issue.code === z.ZodIssueCode.invalid_string &&
    issue.validation === 'regex'
  ) {
    return { message: t('validation.code_pattern') };
  }
  return { message: t('validation.code_required') };
};

export const zCode: typeof z.string = (params) =>
  z
    .string({
      errorMap: zCodeErrorMap,
      ...params,
    })
    .min(1)
    .max(CODE_MAX_LENGTH)
    .regex(/^\w*$/);

export const zCodeOrEmpty: typeof z.string = (params) =>
  z
    .string({
      errorMap: zCodeErrorMap,
      ...params,
    })
    .max(CODE_MAX_LENGTH)
    .regex(/^$|^\w*$/);

export const zCodeWithoutLeadingNumber: typeof z.string = (
  params = errorMap((issue, ctx) => {
    if (
      issue.code === z.ZodIssueCode.invalid_string &&
      issue.validation === 'regex'
    ) {
      return t('validation.code_pattern_without_leading_number');
    }
    return zCodeErrorMap(issue, ctx).message;
  }),
) => zCode(params).regex(/^[A-Za-z_].*$/);

export const zCodeWithoutLeadingNumberOrEmpty = zCodeOrEmpty(
  errorMap((issue) => {
    if (
      issue.code === z.ZodIssueCode.invalid_string &&
      issue.validation === 'regex'
    ) {
      return t('validation.code_pattern_without_leading_number');
    }
    if (issue.code === z.ZodIssueCode.too_big) {
      return t('validation.too_big', { max: issue.maximum });
    }
  }),
)
  .regex(/^$|^[A-Za-z_].*$/)
  .optional();

const getZStringWithMaxLength =
  (maxLength: number): typeof z.string =>
  (params) =>
    z
      .string(
        errorMap((issue, _ctx) =>
          issue.code === z.ZodIssueCode.too_big
            ? t('validation.too_big', { max: issue.maximum })
            : params?.errorMap?.(issue, _ctx).message,
        ),
      )
      .max(maxLength);

export const zName = getZStringWithMaxLength(NAME_MAX_LENGTH);
export const zDescription = getZStringWithMaxLength(DESCRIPTION_MAX_LENGTH);
